<script setup lang="ts">
/**
 * Module: web/src/views/admin/AdminUsersView.vue
 * Author: Salvo Praxis (w/ AI pair programming partner Claude Code by Anthropic)
 * Version: 1.4.0
 *
 * v1.4.0 (contract admin-people-api v1.0.0 §7): a quiet "No person" chip on
 * rows whose person_id is null -- the pre-v1.4.0 logins nothing can
 * attribute to, fixable from the editor's Linked person field. The editor's
 * people-changed event reloads the people list.
 *
 * v1.3.0 (P8 polish): active/role/must-change badges -> shared .chip /
 * .tag-mini; .thead-row / .row-hover table atoms + card shadow; loading ->
 * skeleton; empty state -> .empty-state pattern.
 * Date: 2026-07-09
 *
 * User admin CRUD (doc 04 sect 9.2, gated user_admin). Users list with
 * columns Name / Email / Roles / Active / Last Login / Must-Change + row
 * actions Edit / Deactivate|Reactivate / Reset PW / Welcome. Mobile-capable
 * per the three-tier mandate: collapses to stacked cards below md, actions
 * stay large tap targets. Editor modal: Profile / Roles / Access / Audit tabs.
 *
 * v1.1.0 desktop-table layout fix: the page container spans the full width
 * (was max-w-6xl, which stranded ~1/3 of a wide desktop while the table
 * wrapped cell text mid-word); text cells are whitespace-nowrap and the
 * table scrolls horizontally INSIDE its own overflow-x-auto wrapper when
 * narrow -- the page itself never scrolls sideways. Mobile keeps the
 * stacked-card mode.
 *
 * v1.2.0 (P9 R3, contract user-onboarding-mail): per-user "Welcome" action
 * (active users only) -- explicit confirm dialog (it rotates the password,
 * so a two-tap arm is not enough warning) -> POST send-welcome -> success
 * toast; plus the welcome-result toast for the create-form checkbox path.
 */
import { onMounted, ref } from 'vue';
import { apiGet, apiPost, unwrapList, ApiError } from '@/composables/useApi';
import type { AdminPerson, AdminRole, AdminUser } from '@/types/api';
import { fmtDateTime } from '@/lib/format';
import UserEditorModal from '@/components/admin/UserEditorModal.vue';
import BaseModal from '@/components/BaseModal.vue';
import AppIcon from '@/components/AppIcon.vue';

const users = ref<AdminUser[]>([]);
const roles = ref<AdminRole[]>([]);
const people = ref<AdminPerson[]>([]);
const loading = ref(true);
const errorMessage = ref('');

const editorOpen = ref(false);
const editingUserId = ref<number | null>(null);

/** Two-tap confirm state: `${action}:${id}` armed for a second tap. */
const armedAction = ref<string | null>(null);
const actionBusy = ref(false);

/** One-time temp password surfaced by create/reset (shown once, never stored). */
const tempPassword = ref<string | null>(null);

function normalizeRole(raw: Record<string, unknown>): AdminRole {
  const caps = raw.capabilities ?? raw.caps ?? raw.default_caps ?? [];
  return {
    id: Number(raw.id),
    key: String(raw.key ?? ''),
    label: String(raw.label ?? raw.key ?? ''),
    description: typeof raw.description === 'string' ? raw.description : null,
    capabilities: Array.isArray(caps) ? (caps as string[]) : [],
  };
}

async function load() {
  loading.value = true;
  errorMessage.value = '';
  try {
    const [usersBody, rolesBody, peopleBody] = await Promise.all([
      apiGet<unknown>('/admin/users'),
      apiGet<unknown>('/admin/roles'),
      apiGet<unknown>('/admin/people'),
    ]);
    users.value = unwrapList<AdminUser>(usersBody, 'users');
    roles.value = unwrapList<Record<string, unknown>>(rolesBody, 'roles').map(normalizeRole);
    people.value = unwrapList<AdminPerson>(peopleBody, 'people');
  } catch {
    errorMessage.value = 'Could not load users.';
  } finally {
    loading.value = false;
  }
}

onMounted(load);

function openCreate() {
  editingUserId.value = null;
  editorOpen.value = true;
}

function openEdit(u: AdminUser) {
  editingUserId.value = u.id;
  editorOpen.value = true;
}

/** Two-tap destructive confirm: first tap arms, second tap fires. */
async function runArmed(action: 'deactivate' | 'reactivate' | 'reset-password', u: AdminUser) {
  const key = `${action}:${u.id}`;
  if (armedAction.value !== key) {
    armedAction.value = key;
    setTimeout(() => {
      if (armedAction.value === key) armedAction.value = null;
    }, 4000);
    return;
  }
  armedAction.value = null;
  actionBusy.value = true;
  errorMessage.value = '';
  try {
    const resp = await apiPost<Record<string, unknown> | undefined>(`/admin/users/${u.id}/${action}`);
    if (action === 'reset-password') {
      const temp = resp && typeof resp === 'object' ? resp.temp_password : undefined;
      tempPassword.value = typeof temp === 'string' && temp
        ? temp
        : null;
      if (tempPassword.value === null) {
        // Backend chose not to return the temp password in-band -- still confirm the reset.
        errorMessage.value = '';
        alertResetDone.value = true;
        setTimeout(() => (alertResetDone.value = false), 4000);
      }
    }
    await load();
  } catch {
    errorMessage.value = `Action failed (${action.replace('-', ' ')}).`;
  } finally {
    actionBusy.value = false;
  }
}

const alertResetDone = ref(false);

// ---- welcome email (contract user-onboarding-mail sect 2) -------------------
/** User awaiting welcome-send confirmation (drives the confirm dialog). */
const welcomeTarget = ref<AdminUser | null>(null);
const welcomeBusy = ref(false);
/** Success toast text ('' = hidden) -- shared by row action + create checkbox. */
const welcomeToast = ref('');

function askSendWelcome(u: AdminUser) {
  welcomeTarget.value = u;
}

async function confirmSendWelcome() {
  const u = welcomeTarget.value;
  if (!u || welcomeBusy.value) return;
  welcomeBusy.value = true;
  errorMessage.value = '';
  try {
    const resp = await apiPost<{ sent_to?: string }>(`/admin/users/${u.id}/send-welcome`);
    welcomeTarget.value = null;
    showWelcomeToast(resp?.sent_to ?? u.email);
    await load();
  } catch (e) {
    welcomeTarget.value = null;
    errorMessage.value = e instanceof ApiError && e.code === 'mail_failed'
      ? 'Welcome email could not be sent (mail path down?) — nothing was changed.'
      : e instanceof ApiError && e.code === 'user_inactive'
        ? 'That user is deactivated — reactivate before sending a welcome email.'
        : 'Could not send the welcome email.';
  } finally {
    welcomeBusy.value = false;
  }
}

function showWelcomeToast(email: string) {
  welcomeToast.value = `Welcome email sent to ${email}. They must set a new password at first login.`;
  setTimeout(() => (welcomeToast.value = ''), 6000);
}

/** Create-form checkbox result (emitted by UserEditorModal). */
function onWelcomeResult(payload: { sent: boolean; email: string }) {
  if (payload.sent) showWelcomeToast(payload.email);
  else errorMessage.value = `User created, but the welcome email to ${payload.email} failed — use the Welcome action to retry.`;
}

function roleBadges(u: AdminUser): string[] {
  return u.roles ?? [];
}
</script>

<template>
  <div class="px-4 py-5 md:p-6 w-full">
    <div class="flex items-center justify-between gap-3 mb-4">
      <div>
        <h1 class="text-xl font-bold text-fg">User Admin</h1>
        <p class="text-sm text-fg-muted">Accounts, roles, and per-user access.</p>
      </div>
      <button class="btn-primary !h-11 !px-4 text-sm" @click="openCreate">Add user</button>
    </div>

    <div v-if="errorMessage" class="alert-error mb-4" role="alert">{{ errorMessage }}</div>
    <div v-if="alertResetDone" class="alert-success mb-4" role="status">
      Password reset. The user must change it at next login.
    </div>
    <div v-if="welcomeToast" class="alert-success mb-4" role="status">{{ welcomeToast }}</div>

    <div
      v-if="loading"
      class="bg-card border border-border-c rounded-lg p-4 space-y-3"
      aria-label="Loading users"
    >
      <div v-for="i in 6" :key="`sk-${i}`" class="skeleton h-9" />
    </div>

    <template v-else>
      <!-- Mobile: stacked cards (doc 04 sect 9.2) -->
      <div class="md:hidden space-y-3">
        <div v-for="u in users" :key="u.id" class="bg-card border border-border-c rounded-lg p-4 shadow-card">
          <div class="flex items-start justify-between gap-2">
            <div class="min-w-0">
              <div class="font-semibold text-fg truncate">{{ u.name }}</div>
              <div class="text-xs text-fg-muted truncate">{{ u.email }}</div>
            </div>
            <span
              class="chip flex-shrink-0"
              :class="u.active ? 'chip-good' : 'chip-bad'"
            >{{ u.active ? 'Active' : 'Inactive' }}</span>
          </div>
          <div class="mt-2 flex flex-wrap gap-1">
            <span
              v-for="r in roleBadges(u)"
              :key="r"
              class="tag-mini"
            >{{ r }}</span>
            <span v-if="u.must_change_password" class="tag-mini !bg-accent-warm/15 !text-accent-warm">
              must change pw
            </span>
            <span v-if="u.person_id === null" class="tag-mini !bg-accent-warm/15 !text-accent-warm" title="Not linked to a person — entries cannot attribute">
              no person
            </span>
          </div>
          <div class="mt-1.5 text-xs text-fg-muted">Last login: {{ fmtDateTime(u.last_login_at) }}</div>
          <div class="mt-3 grid grid-cols-2 gap-2">
            <button class="btn-secondary !h-11 text-sm" @click="openEdit(u)">Edit</button>
            <button
              class="text-sm !h-11"
              :class="u.active ? 'btn-danger' : 'btn-secondary'"
              :disabled="actionBusy"
              @click="runArmed(u.active ? 'deactivate' : 'reactivate', u)"
            >
              {{ armedAction === `${u.active ? 'deactivate' : 'reactivate'}:${u.id}` ? 'Sure?' : u.active ? 'Deactivate' : 'Reactivate' }}
            </button>
            <button
              class="btn-secondary !h-11 text-sm"
              :disabled="actionBusy"
              @click="runArmed('reset-password', u)"
            >
              {{ armedAction === `reset-password:${u.id}` ? 'Sure?' : 'Reset PW' }}
            </button>
            <button
              v-if="u.active"
              class="btn-secondary !h-11 text-sm"
              :disabled="actionBusy || welcomeBusy"
              @click="askSendWelcome(u)"
            >
              Send welcome
            </button>
          </div>
        </div>
      </div>

      <!-- Desktop: table -->
      <div class="hidden md:block bg-card border border-border-c rounded-lg shadow-card overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="thead-row">
                <th class="px-4 py-3 font-medium whitespace-nowrap">Name</th>
                <th class="px-4 py-3 font-medium whitespace-nowrap">Email</th>
                <th class="px-4 py-3 font-medium whitespace-nowrap">Roles</th>
                <th class="px-4 py-3 font-medium whitespace-nowrap">Active</th>
                <th class="px-4 py-3 font-medium whitespace-nowrap">Last login</th>
                <th class="px-4 py-3 font-medium whitespace-nowrap">Must change</th>
                <th class="px-4 py-3 font-medium text-right whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="u in users" :key="u.id" class="border-b border-border-c/60 last:border-0 row-hover">
                <td class="px-4 py-2.5 text-fg font-medium whitespace-nowrap">
                  {{ u.name }}
                  <span v-if="u.person_id === null" class="tag-mini !bg-accent-warm/15 !text-accent-warm ml-1.5 align-middle" title="Not linked to a person — entries cannot attribute">no person</span>
                </td>
                <td class="px-4 py-2.5 text-fg-muted whitespace-nowrap">{{ u.email }}</td>
                <td class="px-4 py-2.5">
                  <div class="flex flex-wrap gap-1">
                    <span
                      v-for="r in roleBadges(u)"
                      :key="r"
                      class="tag-mini"
                    >{{ r }}</span>
                  </div>
                </td>
                <td class="px-4 py-2.5">
                  <span :class="u.active ? 'text-accent-good' : 'text-accent-bad'">
                    {{ u.active ? 'Yes' : 'No' }}
                  </span>
                </td>
                <td class="px-4 py-2.5 text-fg-muted whitespace-nowrap">{{ fmtDateTime(u.last_login_at) }}</td>
                <td class="px-4 py-2.5">
                  <span v-if="u.must_change_password" class="text-accent-warm">Yes</span>
                  <span v-else class="text-fg-muted">No</span>
                </td>
                <td class="px-4 py-2.5 whitespace-nowrap">
                  <div class="flex gap-1.5 justify-end">
                    <button class="btn-secondary !h-9 !px-3 text-xs" @click="openEdit(u)">Edit</button>
                    <button
                      class="!h-9 !px-3 text-xs"
                      :class="u.active ? 'btn-danger' : 'btn-secondary'"
                      :disabled="actionBusy"
                      @click="runArmed(u.active ? 'deactivate' : 'reactivate', u)"
                    >
                      {{ armedAction === `${u.active ? 'deactivate' : 'reactivate'}:${u.id}` ? 'Confirm?' : u.active ? 'Deactivate' : 'Reactivate' }}
                    </button>
                    <button
                      class="btn-secondary !h-9 !px-3 text-xs"
                      :disabled="actionBusy"
                      @click="runArmed('reset-password', u)"
                    >
                      {{ armedAction === `reset-password:${u.id}` ? 'Confirm?' : 'Reset PW' }}
                    </button>
                    <button
                      v-if="u.active"
                      class="btn-secondary !h-9 !px-3 text-xs"
                      :disabled="actionBusy || welcomeBusy"
                      @click="askSendWelcome(u)"
                    >
                      Welcome
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-if="users.length === 0" class="empty-state mt-3">
        <div class="empty-state-icon"><AppIcon name="users" class="w-5 h-5" /></div>
        <p class="empty-state-title">No users yet</p>
        <p class="empty-state-hint">Create the first account with "Add user" above.</p>
      </div>
    </template>

    <UserEditorModal
      :open="editorOpen"
      :user-id="editingUserId"
      :roles="roles"
      :people="people"
      @close="editorOpen = false"
      @saved="load"
      @people-changed="load"
      @temp-password="(pw: string) => (tempPassword = pw)"
      @welcome-result="onWelcomeResult"
    />

    <!-- Send-welcome confirm (contract sect 2: rotates the password, so an
         explicit dialog spells out the consequence before firing) -->
    <BaseModal
      :open="welcomeTarget !== null"
      title="Send welcome email"
      max-width="md:max-w-md"
      @close="welcomeTarget = null"
    >
      <p class="text-sm text-fg">
        Generates a <span class="font-semibold">NEW temporary password</span> and emails it to
        <span class="font-semibold">{{ welcomeTarget?.email }}</span>. Continue?
      </p>
      <p class="mt-2 text-xs text-fg-muted">
        Their current password stops working and they must set a new one at first login.
      </p>
      <template #footer>
        <div class="flex gap-3 justify-end">
          <button class="btn-secondary" :disabled="welcomeBusy" @click="welcomeTarget = null">Cancel</button>
          <button class="btn-primary" :disabled="welcomeBusy" @click="confirmSendWelcome">
            {{ welcomeBusy ? 'Sending…' : 'Send email' }}
          </button>
        </div>
      </template>
    </BaseModal>

    <!-- One-time temp password (shown once, never stored) -->
    <BaseModal :open="tempPassword !== null" title="Temporary password" max-width="md:max-w-md" @close="tempPassword = null">
      <p class="text-sm text-fg-muted mb-4">
        Hand this to the user out-of-band. It is shown <span class="font-semibold text-fg">once</span> and
        they must change it at first login.
      </p>
      <div class="bg-input border border-border-c rounded-lg px-4 py-5 text-center">
        <code class="text-xl font-bold tracking-wider text-fg break-all">{{ tempPassword }}</code>
      </div>
      <template #footer>
        <div class="flex justify-end">
          <button class="btn-primary" @click="tempPassword = null">Done — I've noted it</button>
        </div>
      </template>
    </BaseModal>
  </div>
</template>
